import React from 'react';
import { Github, Linkedin, Mail } from 'lucide-react';
import styles from './Portfolio.module.css';
import Project from './Project';

const Portfolio = ({ darkMode, toggleDarkMode }) => {
  const projects = [
    {
      id: 'resonate-fm',
      title: 'Resonate',
      subtitle: 'Music discovery platform',
      tech: ['React', 'Next.js 16', 'Supabase', 'TypeScript'],
      description: 'A social music platform exploring how people discover albums through community rather than algorithms. Launched in November 2025, now 35+ weekly active users logging and reviewing music together.',
      keyPoints: [
        'Designed mobile-first interface prioritizing quick music logging and social exploration',
        'Built intelligent activity feed with optimistic UI updates for instant feedback',
        'Implemented Row-Level Security policies for privacy-first data access',
        'Created real-time messaging to foster organic music conversations'
      ],
      currentChallenge: 'Building the product is one thing, learning to grow it is another.',
      images: [
        '/images/resonate-popular.png',
        '/images/resonate-feed.png',
        '/images/resonate-profile-mobile.png'
      ],
      liveUrl: 'https://resonatefm.app'
    },
    {
      id: 'whatsyourtune',
      title: 'whatsyourtune',
      subtitle: 'Emotion-driven music generator',
      tech: ['Python', 'TensorFlow', 'Google Magenta.js'],
      description: 'An experiment in emotion-responsive technology. Uses facial recognition to detect your current emotion and generates a personalized piano melody to match your mood—bridging computer vision, generative music, and human-computer interaction.',
      keyPoints: [
        'Designed intuitive interface where users simply look at their camera and receive instant musical feedback',
        'Mapped emotion states to musical parameters (happy → major key, upbeat tempo; sad → slower, minor melodies)',
        'Integrated TensorFlow emotion detection with Google Magenta\'s music generation in real-time',
        'Optimized performance pipeline to maintain responsive, fluid user experience'
      ],
      images: [
        '/images/happy_whatsyourtune.png',
        '/images/fear_whatsyourtune.png'
      ],
      liveUrl: 'https://whatsyourtune.vercel.app',
      githubUrl: 'https://github.com/melihonat/whatsyourtune',
      videoUrl: 'https://www.youtube.com/embed/iltL_NA40os'
    }
  ];

  return (
    <div className={styles.portfolio}>
      {/* Dark Mode Toggle */}
      <button
        className={styles.darkModeToggle}
        onClick={toggleDarkMode}
        aria-label="Toggle dark mode"
      >
        {darkMode ? '☀️' : '🌙'}
      </button>

      {/* Hero Section */}
      <header className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.name}>Melih Onat</h1>
          <p className={styles.title}>Creative technologist building products at the intersection of media and technology</p>
          <p className={styles.availability}>
            Digital Media graduate (M.A., TMU 2026) · Seeking full-stack or frontend engineering roles · Toronto or remote
          </p>

          {/* Social Links */}
          <div className={styles.socialLinks}>
            <a
              href="https://github.com/melihonat"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <Github size={20} />
            </a>
            <a
              href="https://www.linkedin.com/in/melih-onat/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <Linkedin size={20} />
            </a>
            <a
              href="mailto:melih.onat@torontomu.ca"
              aria-label="Email"
            >
              <Mail size={20} />
            </a>
          </div>
        </div>
      </header>

      {/* About Section */}
      <section className={styles.about}>
        <h2 className={styles.sectionTitle}>About</h2>
        <div className={styles.aboutContent}>
          <p>
            I'm a Digital Media graduate student focused on building technology that enhances creative expression and human connection. My background in media studies gives me insight into user behavior and aesthetic experience, while my self-taught engineering skills let me bring ideas to life.
          </p>
          <p>
            I'm particularly drawn to music technology and social platforms—spaces where design, emotion, and interaction come together. Both of my projects reflect this: Resonate explores how people discover music through community rather than algorithms, and whatsyourtune bridges emotion recognition with generative music.
          </p>
          <p>
            I'm looking for roles where I can bridge the gap between design thinking and technical implementation, ideally at companies building consumer-facing products that people genuinely enjoy using.
          </p>
        </div>
      </section>

      {/* Skills Section */}
      <section className={styles.skills}>
        <h2 className={styles.sectionTitle}>Skills</h2>
        <div className={styles.skillsGrid}>
          <div className={styles.skillCategory}>
            <h3 className={styles.skillCategoryTitle}>Engineering</h3>
            <div className={styles.skillTags}>
              <span className={styles.skillTag}>JavaScript / TypeScript</span>
              <span className={styles.skillTag}>React</span>
              <span className={styles.skillTag}>Next.js</span>
              <span className={styles.skillTag}>Node.js</span>
              <span className={styles.skillTag}>Python</span>
              <span className={styles.skillTag}>PostgreSQL</span>
              <span className={styles.skillTag}>Supabase</span>
              <span className={styles.skillTag}>REST APIs</span>
              <span className={styles.skillTag}>Git</span>
              <span className={styles.skillTag}>Google Cloud</span>
              <span className={styles.skillTag}>Vercel</span>
            </div>
          </div>
          <div className={styles.skillCategory}>
            <h3 className={styles.skillCategoryTitle}>Design & Media</h3>
            <div className={styles.skillTags}>
              <span className={styles.skillTag}>UX Design</span>
              <span className={styles.skillTag}>Visual Design</span>
              <span className={styles.skillTag}>Typography</span>
              <span className={styles.skillTag}>User Research</span>
              <span className={styles.skillTag}>Responsive Design</span>
              <span className={styles.skillTag}>Accessibility</span>
            </div>
          </div>
          <div className={styles.skillCategory}>
            <h3 className={styles.skillCategoryTitle}>Creative Technology</h3>
            <div className={styles.skillTags}>
              <span className={styles.skillTag}>TensorFlow</span>
              <span className={styles.skillTag}>Google Magenta.js</span>
              <span className={styles.skillTag}>Real-time ML</span>
              <span className={styles.skillTag}>Interactive Media</span>
              <span className={styles.skillTag}>Web Audio API</span>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <main className={styles.projects}>
        {projects.map((project, index) => (
          <Project
            key={project.id}
            project={project}
            index={index}
          />
        ))}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <p className={styles.footerText}>
          Built with React
        </p>
      </footer>
    </div>
  );
};

export default Portfolio;
